<template>
    <span class="opc-curve-settings">
        <b-button :id="targetId" size="sm" variant="outline-secondary" :pressed="show">
            <i class="fa fa-fw fa-sliders"></i>
            <span class="ml-1">{{ $t('label.sith.tpm.opcPointCurve.settings') }}</span>
            <span class="text-muted ml-1">· {{ summary }}</span>
            <i class="fa fa-fw fa-caret-down"></i>
        </b-button>
        <b-popover :id="popoverId" :target="targetId" triggers="click" placement="bottomleft" boundary="window"
                   custom-class="opc-curve-settings-popover" :show.sync="show">
            <div class="opc-curve-settings__panel">
                <div class="form-group mb-2">
                    <label class="small text-muted mb-1">{{ $t('label.sith.tpm.opcPointCurve.span') }}</label>
                    <div class="d-flex">
                        <input type="number" min="0" step="any" class="form-control form-control-sm mr-1"
                               :placeholder="$t('label.sith.tpm.opcPointCurve.summaryFull')"
                               v-model="draft.span" @input="emitPatch">
                        <select class="custom-select custom-select-sm opc-curve-settings__unit"
                                v-model="draft.spanUnit" @change="emitPatch">
                            <option v-for="unit in spanUnits" :key="unit" :value="unit">
                                {{ $t('label.sith.tpm.opcPointCurve.spanUnit.' + unit) }}
                            </option>
                        </select>
                    </div>
                </div>
                <div class="form-group mb-2">
                    <label class="small text-muted mb-1">{{ $t('label.sith.tpm.opcPointCurve.yRange') }}</label>
                    <div class="d-flex align-items-center">
                        <input type="number" step="any" class="form-control form-control-sm"
                               :class="{'is-invalid': invalid}"
                               :placeholder="$t('label.sith.tpm.opcPointCurve.ymin')"
                               v-model="draft.ymin" @input="emitPatch">
                        <span class="mx-1 text-muted">~</span>
                        <input type="number" step="any" class="form-control form-control-sm"
                               :class="{'is-invalid': invalid}"
                               :placeholder="$t('label.sith.tpm.opcPointCurve.ymax')"
                               v-model="draft.ymax" @input="emitPatch">
                    </div>
                    <div v-if="invalid" class="small text-danger mt-1">
                        {{ $t('label.sith.tpm.opcPointCurve.yRangeInvalid') }}
                    </div>
                    <div v-else class="small text-muted mt-1">{{ $t('label.sith.tpm.opcPointCurve.yRangeTip') }}</div>
                </div>
                <div class="form-group mb-2">
                    <label class="small text-muted mb-1">{{ $t('label.sith.tpm.opcPointCurve.yunit') }}</label>
                    <input type="text" maxlength="20" class="form-control form-control-sm"
                           :placeholder="autoUnitText"
                           v-model="draft.yunit" @input="emitPatch">
                    <div class="small text-muted mt-1">{{ $t('label.sith.tpm.opcPointCurve.yunitTip') }}</div>
                </div>
                <div class="form-group mb-2">
                    <label class="small text-muted mb-1">{{ $t('label.sith.tpm.opcPointCurve.xinterval') }}</label>
                    <input type="number" min="0" step="any" class="form-control form-control-sm"
                           :placeholder="$t('label.sith.tpm.opcPointCurve.auto')"
                           v-model="draft.xinterval" @input="emitPatch">
                    <div class="small mt-1" :class="xintervalFallback ? 'text-warning' : 'text-muted'">
                        {{ xintervalFallback ? $t('label.sith.tpm.opcPointCurve.xintervalFallback')
                            : $t('label.sith.tpm.opcPointCurve.xintervalTip') }}
                    </div>
                </div>
                <div class="d-flex justify-content-between align-items-center">
                    <b-button size="sm" variant="link" class="px-0" @click="restoreDefault">
                        <i class="fa fa-fw fa-undo"></i>{{ $t('label.sith.tpm.opcPointCurve.restoreDefault') }}
                    </b-button>
                    <b-button size="sm" variant="outline-secondary" @click="show = false">
                        {{ $t('label.sith.tpm.opcPointCurve.close') }}
                    </b-button>
                </div>
            </div>
        </b-popover>
    </span>
</template>

<script>
/* eslint-env browser */
import {defaultCurveSettings, spanMs, toNumberOrNull, xIntervalMs, yRangeInvalid} from "./opcCurveSettings";

let seq = 0;

/**
 * 「曲线设置 ▾」：默认折叠的 popover，改动即时生效（v-model = queryState.settings）。
 * 按钮上带当前设置摘要；点 popover 外部收起。
 */
export default {
    name: "OpcCurveSettingsPopover",
    props: {
        // { span, spanUnit, ymin, ymax, yunit, xinterval }
        value: {
            type: Object,
            required: true
        },
        // 勾选点位的共同单位（手动单位留空时纵轴显示它），只用于输入框占位提示
        autoUnit: {
            type: String,
            default: ''
        },
        // 设了刻度间隔但当前视窗刻度过多，图表已按默认刻度显示
        xintervalFallback: {
            type: Boolean,
            default: false
        }
    },
    data() {
        seq += 1;
        return {
            targetId: 'opc-curve-settings-btn-' + seq,
            popoverId: 'opc-curve-settings-pop-' + seq,
            show: false,
            spanUnits: ['m', 'h', 'd'],
            // 输入框草稿：未输完的数字（如「-」）不回写成 null 打断输入
            draft: this.toDraft(this.value)
        };
    },
    computed: {
        invalid() {
            return yRangeInvalid(this.value);
        },
        autoUnitText() {
            const auto = this.$t('label.sith.tpm.opcPointCurve.auto');
            return this.autoUnit ? auto + '：' + this.autoUnit : auto;
        },
        summary() {
            const parts = [];
            if (spanMs(this.value) === null) {
                parts.push(this.$t('label.sith.tpm.opcPointCurve.summaryFull'));
            } else {
                parts.push(this.$t('label.sith.tpm.opcPointCurve.summarySpan', {
                    span: this.value.span,
                    unit: this.$t('label.sith.tpm.opcPointCurve.spanUnit.' + this.value.spanUnit)
                }));
            }
            const ymin = toNumberOrNull(this.value.ymin);
            const ymax = toNumberOrNull(this.value.ymax);
            if (ymin !== null || ymax !== null) {
                const auto = this.$t('label.sith.tpm.opcPointCurve.auto');
                parts.push(this.$t('label.sith.tpm.opcPointCurve.summaryY', {
                    min: ymin === null ? auto : ymin,
                    max: ymax === null ? auto : ymax
                }));
            }
            if (this.value.yunit) {
                parts.push(this.value.yunit);
            }
            if (xIntervalMs(this.value) !== null) {
                parts.push(this.$t('label.sith.tpm.opcPointCurve.summaryXInterval', {hours: this.value.xinterval})
                    + (this.xintervalFallback ? '（' + this.$t('label.sith.tpm.opcPointCurve.auto') + '）' : ''));
            }
            return parts.join(' · ');
        }
    },
    watch: {
        value: {
            deep: true,
            handler(value) {
                // 外部改了（恢复默认 / S2 选用已保存查询），草稿与之不一致才重置，避免输入中被覆盖
                if (!this.sameAsDraft(value)) {
                    this.draft = this.toDraft(value);
                }
            }
        },
        show(show) {
            if (show) {
                document.addEventListener('mousedown', this.onDocumentMousedown, true);
            } else {
                document.removeEventListener('mousedown', this.onDocumentMousedown, true);
            }
        }
    },
    beforeDestroy() {
        document.removeEventListener('mousedown', this.onDocumentMousedown, true);
    },
    methods: {
        toDraft(value) {
            const v = value || defaultCurveSettings();
            return {
                span: v.span === null || v.span === undefined ? '' : v.span,
                spanUnit: v.spanUnit || 'h',
                ymin: v.ymin === null || v.ymin === undefined ? '' : v.ymin,
                ymax: v.ymax === null || v.ymax === undefined ? '' : v.ymax,
                yunit: v.yunit || '',
                xinterval: v.xinterval === null || v.xinterval === undefined ? '' : v.xinterval
            };
        },
        fromDraft() {
            return {
                span: toNumberOrNull(this.draft.span),
                spanUnit: this.draft.spanUnit,
                ymin: toNumberOrNull(this.draft.ymin),
                ymax: toNumberOrNull(this.draft.ymax),
                yunit: (this.draft.yunit || '').trim(),
                xinterval: toNumberOrNull(this.draft.xinterval)
            };
        },
        sameAsDraft(value) {
            const d = this.fromDraft();
            return !!value && d.span === toNumberOrNull(value.span) && d.spanUnit === value.spanUnit
                && d.ymin === toNumberOrNull(value.ymin) && d.ymax === toNumberOrNull(value.ymax)
                && d.yunit === (value.yunit || '') && d.xinterval === toNumberOrNull(value.xinterval);
        },
        emitPatch() {
            this.$emit('input', this.fromDraft());
        },
        restoreDefault() {
            this.draft = this.toDraft(defaultCurveSettings());
            this.$emit('input', defaultCurveSettings());
        },
        onDocumentMousedown(event) {
            const button = document.getElementById(this.targetId);
            const popover = document.getElementById(this.popoverId);
            const target = event.target;
            if ((button && button.contains(target)) || (popover && popover.contains(target))) {
                return;
            }
            this.show = false;
        }
    }
};
</script>

<style scoped>
.opc-curve-settings__panel {
    min-width: 260px;
}

.opc-curve-settings__unit {
    flex: 0 0 auto;
    width: auto;
}
</style>
